import { useMemo, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Plus, X, AlertCircle, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { type BusinessHoursConfig, validateBusinessHours } from "@/lib/business-hours";

const CURATED_TIMEZONES: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern (US/Canada) — America/New_York" },
  { value: "America/Chicago", label: "Central (US/Canada) — America/Chicago" },
  { value: "America/Denver", label: "Mountain (US/Canada) — America/Denver" },
  { value: "America/Los_Angeles", label: "Pacific (US/Canada) — America/Los_Angeles" },
  { value: "America/Phoenix", label: "Arizona (no DST) — America/Phoenix" },
  { value: "America/Anchorage", label: "Alaska — America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii — Pacific/Honolulu" },
  { value: "America/Toronto", label: "Toronto — America/Toronto" },
  { value: "America/Vancouver", label: "Vancouver — America/Vancouver" },
  { value: "America/Mexico_City", label: "Mexico City — America/Mexico_City" },
  { value: "America/Sao_Paulo", label: "São Paulo — America/Sao_Paulo" },
  { value: "Europe/London", label: "London — Europe/London" },
  { value: "Europe/Dublin", label: "Dublin — Europe/Dublin" },
  { value: "Europe/Paris", label: "Paris — Europe/Paris" },
  { value: "Europe/Berlin", label: "Berlin — Europe/Berlin" },
  { value: "Europe/Madrid", label: "Madrid — Europe/Madrid" },
  { value: "Europe/Amsterdam", label: "Amsterdam — Europe/Amsterdam" },
  { value: "Europe/Stockholm", label: "Stockholm — Europe/Stockholm" },
  { value: "Asia/Kolkata", label: "India — Asia/Kolkata" },
  { value: "Asia/Dubai", label: "Gulf — Asia/Dubai" },
  { value: "Asia/Singapore", label: "Singapore — Asia/Singapore" },
  { value: "Asia/Tokyo", label: "Tokyo — Asia/Tokyo" },
  { value: "Asia/Shanghai", label: "Shanghai — Asia/Shanghai" },
  { value: "Australia/Sydney", label: "Sydney — Australia/Sydney" },
  { value: "Australia/Melbourne", label: "Melbourne — Australia/Melbourne" },
  { value: "Pacific/Auckland", label: "Auckland — Pacific/Auckland" },
  { value: "UTC", label: "UTC — UTC" },
];

const DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];

function detectBrowserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

function minutesToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

interface BusinessHoursPickerProps {
  value: BusinessHoursConfig | null;
  onChange: (value: BusinessHoursConfig | null) => void;
}

export default function BusinessHoursPicker({ value, onChange }: BusinessHoursPickerProps) {
  const enabled = value !== null;
  const [showAllZones, setShowAllZones] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);

  // Default config when toggling on.
  const defaultConfig = (): BusinessHoursConfig => ({
    tz: detectBrowserTz(),
    days: [1, 2, 3, 4, 5],
    windows: [{ start: 9 * 60, end: 17 * 60 }],
  });

  const cfg = value ?? defaultConfig();

  const handleToggle = (on: boolean) => {
    onChange(on ? defaultConfig() : null);
  };

  const updateCfg = (patch: Partial<BusinessHoursConfig>) => {
    onChange({ ...cfg, ...patch });
  };

  const updateWindow = (idx: number, patch: { start?: number; end?: number }) => {
    const windows = cfg.windows.map((w, i) => (i === idx ? { ...w, ...patch } : w));
    onChange({ ...cfg, windows });
  };

  const addWindow = () => {
    onChange({ ...cfg, windows: [...cfg.windows, { start: 13 * 60, end: 17 * 60 }] });
  };

  const removeWindow = (idx: number) => {
    const windows = cfg.windows.filter((_, i) => i !== idx);
    onChange({ ...cfg, windows });
  };

  const allZones = useMemo<string[]>(() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
    } catch {
      return [];
    }
  }, []);

  const tzOptions = showAllZones
    ? allZones.map((tz) => ({ value: tz, label: tz }))
    : CURATED_TIMEZONES;

  const validation = enabled ? validateBusinessHours(cfg) : null;
  const errors: string[] = validation && validation.ok === false ? validation.errors : [];

  return (
    <div className="grid gap-3 p-4 border rounded-lg">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Business Hours</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Restrict dispatch to specific days and time windows in a chosen timezone. Outside these hours, the campaign waits and resumes automatically when the next window opens.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggle} aria-label="Enable business hours" />
      </div>

      {enabled && (
        <div className="grid gap-4 mt-2">
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Timezone</Label>
              <button
                type="button"
                onClick={() => setShowAllZones((s) => !s)}
                className="text-xs text-blue-600 hover:underline"
              >
                {showAllZones ? "Show curated list" : "Show all timezones"}
              </button>
            </div>
            <Popover open={tzOpen} onOpenChange={setTzOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={tzOpen}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {tzOptions.find((opt) => opt.value === cfg.tz)?.label ?? cfg.tz}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search timezone..." />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No timezone found.</CommandEmpty>
                    <CommandGroup>
                      {tzOptions.map((opt) => (
                        <CommandItem
                          key={opt.value}
                          value={opt.label}
                          onSelect={() => {
                            updateCfg({ tz: opt.value });
                            setTzOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", cfg.tz === opt.value ? "opacity-100" : "opacity-0")} />
                          {opt.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid gap-2">
            <Label className="text-sm">Days</Label>
            <ToggleGroup
              type="multiple"
              value={cfg.days.map(String)}
              onValueChange={(vals) => {
                const days = vals
                  .map((v) => Number.parseInt(v, 10))
                  .filter((n) => Number.isFinite(n))
                  .sort((a, b) => a - b);
                updateCfg({ days });
              }}
              className="justify-start gap-2 flex-wrap"
            >
              {DAYS.map((d) => (
                <ToggleGroupItem
                  key={d.value}
                  value={String(d.value)}
                  variant="outline"
                  className="h-9 min-w-[3.25rem] px-3 rounded-full border-2 border-muted-foreground/25 bg-background text-muted-foreground font-medium hover:bg-muted hover:text-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=on]:shadow-sm"
                >
                  {d.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              Selected days are highlighted. Click to toggle.
            </p>
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Time windows (campaign-local)</Label>
              <Button type="button" variant="outline" size="sm" onClick={addWindow}>
                <Plus className="w-3 h-3 mr-1" /> Add window
              </Button>
            </div>
            <div className="space-y-2">
              {cfg.windows.map((w, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={minutesToHHMM(w.start)}
                    onChange={(e) => updateWindow(idx, { start: hhmmToMinutes(e.target.value) })}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={minutesToHHMM(w.end)}
                    onChange={(e) => updateWindow(idx, { end: hhmmToMinutes(e.target.value) })}
                    className="w-32"
                  />
                  {cfg.windows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeWindow(idx)}
                      aria-label="Remove window"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Cross-midnight windows aren't supported. To run 6pm–2am, use two windows on adjacent days.
            </p>
          </div>

          {errors.length > 0 && (
            <div className="flex items-start gap-2 p-2 rounded bg-red-50 border border-red-200">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <ul className="text-xs text-red-700 space-y-0.5">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
